/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * AnalyticsPage
 * -------------
 * /feed/analytics — the UpVibe app's Analytics tab on the web (Variant B, as
 * AnalyticsFragment / the iOS AnalyticsView draw it):
 *   - a 30D / 3M / 6M / 1Y / Custom segmented control;
 *   - type chips, only for types the athlete has recorded, shown when 2+;
 *   - a headline card: the period total (distance, or duration for Yoga), the
 *     change vs the previous equal-length period, and a bar chart you can tap
 *     or drag to read one bucket into the headline;
 *   - activities / moving / kcal tiles;
 *   - the activity mix as one segmented bar with a legend.
 *
 * Data: the user's activity rows for the range and for the window before it
 * (useAnalyticsActivities — the same table/fetch the app syncs with),
 * aggregated by analyticsModel with the app's own formulas. The unit follows
 * user_profile.preferred_unit.
 */

import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import FeedIcon from '../../../components/feed/FeedIcon';
import AnalyticsBarChart from '../../../components/feed/AnalyticsBarChart';
import { LoadError } from '../../../components/feed/FeedParts';
import { useFeedUser, useAnalyticsActivities, useUserProfileRow } from '../useFeedData';
import { typeMeta, dayKey } from '../feedModel';
import {
  RANGE_FILTERS, CHART_HINT, initialAnalyticsState, analyticsReducer, currentSpec, previousRange,
  buildAnalytics, rangeTotals, recordedTypes, chartSeries, unitPreference, utcDayMs,
  headlineValue, changeBadge, periodLabel, formatMoving,
} from './analyticsModel';
import '../../../styles/analytics.css';

const CHART_HEIGHT = 172;

/** The custom range picker: two day fields, never past today, end on/after start. */
function CustomRangeForm({ initial, onApply, onCancel }) {
  const today = dayKey(new Date());
  const toDay = (ms) => (ms === null || ms === undefined ? '' : new Date(ms).toISOString().slice(0, 10));
  const [from, setFrom] = useState(toDay(initial?.fromMs));
  const [to, setTo] = useState(toDay(initial?.toMs));
  const firstRef = useRef(null);
  useEffect(() => { firstRef.current?.focus(); }, []);

  let error = '';
  if (from && from > today) error = 'The start date can’t be in the future.';
  else if (to && to > today) error = 'The end date can’t be in the future.';
  else if (from && to && from > to) error = 'The end date must be on or after the start date.';
  const ready = from && to && !error;

  return (
    <form
      className="an-custom"
      aria-label="Custom date range"
      onSubmit={(e) => { e.preventDefault(); if (ready) onApply(utcDayMs(from), utcDayMs(to)); }}
      onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}
    >
      <label>
        <span>From</span>
        <input ref={firstRef} type="date" value={from} max={to || today} onChange={(e) => setFrom(e.target.value)} required />
      </label>
      <label>
        <span>To</span>
        <input type="date" value={to} min={from || undefined} max={today} onChange={(e) => setTo(e.target.value)} required />
      </label>
      <div className="an-custom-act">
        <button type="button" className="fd-btn-sm" onClick={onCancel}>Cancel</button>
        <button type="submit" className="fd-btn-sm an-apply" disabled={!ready}>Apply</button>
      </div>
      {error && <p className="an-custom-err" role="alert">{error}</p>}
    </form>
  );
}

/** The activity mix: one capsule bar split by share, plus a wrapping legend. */
function ActivityMix({ rows, isLoading }) {
  // Every type stays visible however small its share (at least ~2%).
  const weights = rows.map((r) => Math.max(r.pct, 2));
  return (
    <section className="an-card" aria-labelledby="an-mix-h">
      <h2 id="an-mix-h" className="an-h2">Activity mix</h2>
      {rows.length ? (
        <>
          <div className="an-mixbar" aria-hidden="true">
            {rows.map((r, i) => <i key={r.type} className={`an-c${i % 6}`} style={{ flexGrow: weights[i] }} />)}
          </div>
          <ul className="an-legend">
            {rows.map((r, i) => (
              <li key={r.type} title={`${r.count} ${r.count === 1 ? 'activity' : 'activities'}`}>
                <i className={`an-c${i % 6}`} aria-hidden="true" />
                {typeMeta(r.type).label} {r.pct}%
              </li>
            ))}
          </ul>
        </>
      ) : (
        !isLoading && <p className="an-empty-sm">No activities in this range</p>
      )}
    </section>
  );
}

function Tile({ value, label }) {
  return (
    <div className="an-tile">
      <span className="an-tile-v">{value}</span>
      <span className="an-tile-l">{label}</span>
    </div>
  );
}

export default function AnalyticsPage() {
  const user = useFeedUser();
  const [state, dispatch] = useReducer(analyticsReducer, undefined, () => initialAnalyticsState(new Date()));
  const [customOpen, setCustomOpen] = useState(false);
  const [selection, setSelection] = useState({ id: null, index: -1 });
  const [hover, setHover] = useState({ id: null, index: -1 });

  const spec = currentSpec(state);
  const prevSpec = useMemo(() => previousRange(spec), [spec]);

  const data = useAnalyticsActivities(user.userId, spec);
  const prev = useAnalyticsActivities(user.userId, prevSpec);
  const profile = useUserProfileRow(user.userId);
  const unit = unitPreference(profile.data);

  // While a new range loads, the rows on hand still belong to the range shown
  // before it; aggregate them with THAT range so the faded chart under the
  // spinner is the previous chart unchanged (as the app keeps it), not old rows
  // re-bucketed into the new range's granularity.
  const shownSpecRef = useRef(spec);
  if (!data.isLoading) shownSpecRef.current = spec;
  const shownSpec = data.isLoading && data.hasData ? shownSpecRef.current : spec;

  const analytics = useMemo(() => buildAnalytics(data.activities, shownSpec, state.type), [data.activities, shownSpec, state.type]);
  const series = useMemo(() => chartSeries(analytics, { type: state.type, unit }), [analytics, state.type, unit]);
  const current = useMemo(() => rangeTotals(data.activities, shownSpec, state.type), [data.activities, shownSpec, state.type]);
  const previous = useMemo(() => rangeTotals(prev.activities, prevSpec, state.type), [prev.activities, prevSpec, state.type]);
  // Chips for every type seen in either window; the picked one never vanishes.
  const types = useMemo(() => {
    const seen = recordedTypes(data.activities, prev.activities);
    return state.type && !seen.includes(state.type) ? recordedTypes(data.activities, prev.activities, [{ type: state.type }]) : seen;
  }, [data.activities, prev.activities, state.type]);

  // New data clears the selected bar, as setSeries does.
  const seriesId = `${shownSpec.fromIso}|${shownSpec.toIso}|${state.type}|${unit}|${series.points.length}`;
  const selected = selection.id === seriesId && selection.index < series.points.length ? selection.index : -1;
  const hovered = hover.id === seriesId && hover.index < series.points.length ? hover.index : -1;
  // The headline reads the hovered bar while the mouse is on one, else the selected bar.
  const shown = hovered >= 0 ? hovered : selected;
  const selectedPoint = shown >= 0 ? series.points[shown] : null;

  const hasChartData = series.points.some((p) => p.value > 0);
  const swim = state.type === 'swim';
  const total = series.useDuration ? current.movingSeconds / 60 : current.distanceKm * series.factor;
  const head = headlineValue(selectedPoint ? selectedPoint.value : total, { unit: series.unit, useDuration: series.useDuration, swim });
  const badge = selectedPoint || prev.isLoading || data.isLoading ? null
    : series.useDuration ? changeBadge(current.movingSeconds, previous.movingSeconds)
      : changeBadge(current.distanceKm, previous.distanceKm);

  const pickRange = (id) => {
    if (id === 'CUSTOM') { setCustomOpen(true); return; }
    setCustomOpen(false);
    dispatch({ type: 'range', range: id, now: new Date() });
  };
  const rangePressed = (id) => (customOpen ? id === 'CUSTOM' : state.range === id);

  return (
    <div className="an-page">
      <div className="an-seg" role="group" aria-label="Date range">
        {RANGE_FILTERS.map((r) => (
          <button key={r.id} type="button" aria-label={r.label} aria-pressed={rangePressed(r.id)} onClick={() => pickRange(r.id)}>
            {r.short}
          </button>
        ))}
      </div>
      {customOpen && (
        <CustomRangeForm
          initial={state.custom}
          onCancel={() => setCustomOpen(false)}
          onApply={(fromMs, toMs) => { setCustomOpen(false); dispatch({ type: 'custom', fromMs, toMs }); }}
        />
      )}
      {types.length > 1 && (
        <div className="fd-chips an-chips" role="group" aria-label="Activity type">
          <button type="button" className="fd-chip" aria-pressed={!state.type} onClick={() => dispatch({ type: 'activityType', activityType: null })}>All</button>
          {types.map((t) => (
            <button key={t} type="button" className="fd-chip" aria-pressed={state.type === t} onClick={() => dispatch({ type: 'activityType', activityType: t })}>
              <FeedIcon name={typeMeta(t).icon} />{typeMeta(t).label}
            </button>
          ))}
        </div>
      )}

      {data.isError && !data.hasData ? (
        <LoadError what="your analytics" onRetry={data.refetch} />
      ) : (
        <>
          <section className="an-card an-head" aria-label="Headline">
            <div className="an-head-top">
              <div className="an-head-txt">
                <p className="an-eyebrow">{series.metric} · {periodLabel(state)}</p>
                <p className="an-big" aria-live="polite">
                  <span className="an-big-v">{head.value}</span>
                  {head.unit && <span className="an-big-u">{head.unit}</span>}
                </p>
                {selectedPoint && <p className="an-big-sel">{selectedPoint.label}</p>}
              </div>
              {badge && (
                <span className={`an-badge${badge.up ? ' is-up' : ''}`} aria-label={`Change vs previous period: ${badge.text}`}>
                  {badge.text}
                </span>
              )}
            </div>
            <div className={`an-plot${data.isLoading ? ' is-loading' : ''}`} style={{ height: CHART_HEIGHT }} aria-busy={data.isLoading || undefined}>
              {hasChartData ? (
                <AnalyticsBarChart
                  points={series.points}
                  selected={selected}
                  hovered={hovered}
                  onSelect={(index) => setSelection({ id: seriesId, index })}
                  onHover={(index) => setHover({ id: seriesId, index })}
                  height={CHART_HEIGHT}
                  label={`${series.metric} by ${shownSpec.granularity === 'DAILY' ? 'day' : shownSpec.granularity === 'WEEKLY' ? 'week' : 'month'}`}
                />
              ) : (
                !data.isLoading && (
                  <div className="an-chart-empty">
                    <FeedIcon name="insights" />
                    No activity data for this range yet
                  </div>
                )
              )}
              {data.isLoading && <span className="an-spin" role="status" aria-label="Loading analytics" />}
            </div>
            {hasChartData && <p className="an-hint">{CHART_HINT[shownSpec.granularity]}</p>}
            {data.isError && data.hasData && (
              <p className="an-err" role="alert">
                Couldn’t load your analytics. Please try again.
                <button type="button" className="fd-link" onClick={() => data.refetch()}>Retry</button>
              </p>
            )}
          </section>

          <div className="an-tiles">
            <Tile value={current.count.toLocaleString()} label={current.count === 1 ? 'activity' : 'activities'} />
            <Tile value={formatMoving(current.movingSeconds)} label="moving" />
            <Tile value={current.calories.toLocaleString()} label="kcal" />
          </div>

          <ActivityMix rows={analytics.breakdown} isLoading={data.isLoading && !data.hasData} />
        </>
      )}
    </div>
  );
}
